/**
 * Smart contextual push notification prompt.
 *
 * Renders a friendly opt-in card at high-value moments (right after a
 * booking, right after a points purchase). Only shows IF:
 *   - The browser/device supports web push
 *   - Permission has never been requested before (state is 'default' — not
 *     'granted' or 'denied')
 *   - The user doesn't already have an active push subscription
 *   - Either they've never tapped "Not now" for this moment, OR enough
 *     moments have passed since they did (see RESHOW_AFTER)
 *
 * Counter logic: a per-moment counter increments every time the user lands
 * on the success screen. On "Not now", we store the counter value. We
 * re-show once the counter has advanced RESHOW_AFTER beyond that mark.
 * "Yes, turn on" → grant/deny → the browser's own permission state takes
 * over and the card won't reappear regardless of counter.
 */
import React, { useState, useEffect } from 'react';
import {
  isPushSupported,
  pushPermissionState,
  getCurrentSubscription,
  enablePushNotifications,
} from '../lib/push';

const RESHOW_AFTER = 5; // moments since last "Not now" before we re-ask

const COPY = {
  booking: {
    title: 'Get a reminder before your treatment?',
    body: 'We\'ll send a push notification an hour before your booking so you don\'t miss it.',
  },
  pack_purchase: {
    title: 'Get notified when points are about to expire?',
    body: 'We\'ll send a friendly heads-up before any of your points expire so they don\'t go to waste.',
  },
};

export default function PushPromptCard({ moment }) {
  const [show, setShow] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!isPushSupported()) return;
    if (pushPermissionState() !== 'default') return;
    try {
      const countKey = `push_count_${moment}`;
      const dismissedAtKey = `push_dismissed_at_${moment}`;
      // Increment per-moment counter every visit to a success screen
      const newCount = parseInt(localStorage.getItem(countKey) || '0', 10) + 1;
      localStorage.setItem(countKey, String(newCount));

      // If they tapped "Not now" before, suppress until RESHOW_AFTER more
      // moments have happened
      const dismissedAtRaw = localStorage.getItem(dismissedAtKey);
      if (dismissedAtRaw !== null) {
        const dismissedAt = parseInt(dismissedAtRaw, 10);
        if (newCount - dismissedAt < RESHOW_AFTER) return;
      }
    } catch (_) { /* localStorage blocked — show anyway */ }
    // Don't show if they already have an active subscription
    getCurrentSubscription().then(sub => {
      if (!sub) setShow(true);
    });
  }, [moment]);

  function dismiss() {
    try {
      const countKey = `push_count_${moment}`;
      const currentCount = parseInt(localStorage.getItem(countKey) || '0', 10);
      localStorage.setItem(`push_dismissed_at_${moment}`, String(currentCount));
    } catch (_) {}
    setShow(false);
  }

  async function enable() {
    setEnabling(true); setMsg('');
    const result = await enablePushNotifications();
    if (result.ok) {
      // Permission state is now 'granted' + we have a subscription — the
      // useEffect guard handles non-resurfacing automatically.
      setMsg('✅ Notifications enabled');
      setTimeout(() => setShow(false), 1800);
    } else if (result.reason === 'denied') {
      // Permission state is now 'denied' — useEffect guard blocks re-show.
      setMsg('Permission denied — you can turn this on in your device settings later.');
      setTimeout(() => setShow(false), 2800);
    } else if (result.reason === 'dismissed') {
      // User dismissed the OS prompt — don't record dismissal so they get
      // re-prompted at the next high-value moment.
      setMsg('No problem — you can turn this on anytime in Profile.');
      setTimeout(() => setShow(false), 2200);
    } else {
      setMsg('Couldn\'t turn on notifications. Try again from Profile.');
      setTimeout(() => setEnabling(false), 1200);
    }
    setEnabling(false);
  }

  if (!show) return null;
  const copy = COPY[moment] || COPY.booking;

  return (
    <div style={{
      background: '#1A1A1A',
      borderRadius: 12,
      padding: 18,
      marginTop: 24,
      marginBottom: 24,
      textAlign: 'left',
      maxWidth: 420,
      marginLeft: 'auto',
      marginRight: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1 }}>🔔</div>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>{copy.title}</div>
          <div style={{ color: '#aaa', fontSize: '0.82rem', lineHeight: 1.5 }}>{copy.body}</div>
        </div>
      </div>
      {msg && (
        <div style={{ color: '#fff', fontSize: '0.85rem', marginBottom: 10, textAlign: 'center' }}>{msg}</div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={dismiss}
          disabled={enabling}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent', color: '#aaa',
            cursor: enabling ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem', fontWeight: 600,
          }}
        >
          Not now
        </button>
        <button
          type="button"
          onClick={enable}
          disabled={enabling}
          style={{
            flex: 1, padding: '10px', borderRadius: 8, border: 'none',
            background: '#FF6200', color: '#fff',
            cursor: enabling ? 'not-allowed' : 'pointer',
            fontSize: '0.85rem', fontWeight: 700,
          }}
        >
          {enabling ? '…' : 'Yes, turn on'}
        </button>
      </div>
    </div>
  );
}
